import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import {
    adminRmnBrandCategoriesEndpoint,
    adminRmnCategoryEndpoint,
} from "../../../api/endpoints";
import { RmnCategoryNode } from "../../../types/rmn";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { DotLottleLoader } from "../../../components/Loader";
import { IconPencil, IconPlus, IconTrash } from "../../../utils/icons";

interface VisibleRow {
    node: RmnCategoryNode;
    depth: number;
    hasChildren: boolean;
}

// Prune the tree to branches containing a search match. A matching node keeps
// its full subtree (it is the branch the admin searched for).
const filterTree = (nodes: RmnCategoryNode[], q: string): RmnCategoryNode[] => {
    const out: RmnCategoryNode[] = [];
    nodes.forEach((n) => {
        const selfMatch = (n.name || "").toLowerCase().includes(q);
        if (selfMatch) {
            out.push(n);
            return;
        }
        const children = filterTree(n.children || [], q);
        if (children.length) out.push({ ...n, children });
    });
    return out;
};

const flattenVisible = (
    nodes: RmnCategoryNode[],
    depth: number,
    expanded: Set<number>,
    expandAll: boolean,
    out: VisibleRow[]
) => {
    nodes.forEach((n) => {
        const children = n.children || [];
        out.push({ node: n, depth, hasChildren: children.length > 0 });
        if (children.length && (expandAll || expanded.has(n.id))) {
            flattenVisible(children, depth + 1, expanded, expandAll, out);
        }
    });
};

// Flat options for the "Add category" parent picker, indented by depth.
const flattenOptions = (
    nodes: RmnCategoryNode[],
    depth: number,
    out: { id: number; label: string }[]
) => {
    nodes.forEach((n) => {
        out.push({ id: n.id, label: `${"— ".repeat(depth)}${n.name}` });
        if (n.children && n.children.length) flattenOptions(n.children, depth + 1, out);
    });
};

const isExternalUrl = (url?: string | null) =>
    !!url && /^https?:\/\//i.test(url);

interface CategoryTreePanelProps {
    brandId: number;
}

// Internal-admin view of an RMN's crawled category tree: expand/collapse,
// client-side search, rename, activate/deactivate, manual add/delete.
export function CategoryTreePanel({ brandId }: CategoryTreePanelProps) {
    const [tree, setTree] = useState<RmnCategoryNode[]>([]);
    const [loading, setLoading] = useState(true);
    const [showDeactivated, setShowDeactivated] = useState(false);
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [saving, setSaving] = useState(false);

    const [renameTarget, setRenameTarget] = useState<RmnCategoryNode | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [deleteTarget, setDeleteTarget] = useState<RmnCategoryNode | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [addName, setAddName] = useState("");
    const [addParentId, setAddParentId] = useState<number | "">("");

    const fetchTree = useCallback(async () => {
        try {
            const params: Record<string, unknown> = { tree: 1 };
            if (showDeactivated) params.include_deactivated = 1;
            const res = await axiosInstant.get(
                adminRmnBrandCategoriesEndpoint(brandId),
                { params }
            );
            setTree(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            toast.error("Failed to load categories");
        } finally {
            setLoading(false);
        }
    }, [brandId, showDeactivated]);

    useEffect(() => {
        setLoading(true);
        fetchTree();
    }, [fetchTree]);

    const query = search.trim().toLowerCase();
    const rows = useMemo(() => {
        const visible = query ? filterTree(tree, query) : tree;
        const out: VisibleRow[] = [];
        // While searching, show matching branches fully expanded.
        flattenVisible(visible, 0, expanded, !!query, out);
        return out;
    }, [tree, expanded, query]);

    const parentOptions = useMemo(() => {
        const out: { id: number; label: string }[] = [];
        flattenOptions(tree, 0, out);
        return out;
    }, [tree]);

    const toggle = (id: number) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const submitRename = async () => {
        if (!renameTarget || !renameValue.trim()) return;
        setSaving(true);
        try {
            await axiosInstant.patch(adminRmnCategoryEndpoint(renameTarget.id), {
                name: renameValue.trim(),
            });
            toast.success("Category renamed");
            setRenameTarget(null);
            await fetchTree();
        } catch (err) {
            toast.error("Failed to rename category");
        } finally {
            setSaving(false);
        }
    };

    const toggleDeactivated = async (node: RmnCategoryNode) => {
        setSaving(true);
        try {
            await axiosInstant.patch(adminRmnCategoryEndpoint(node.id), {
                deactivated: !node.deactivated,
            });
            toast.success(node.deactivated ? "Category activated" : "Category deactivated");
            await fetchTree();
        } catch (err) {
            toast.error("Failed to update category");
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        try {
            await axiosInstant.delete(adminRmnCategoryEndpoint(deleteTarget.id));
            toast.success("Category deleted");
            setDeleteTarget(null);
            await fetchTree();
        } catch (err) {
            toast.error("Failed to delete category");
        } finally {
            setSaving(false);
        }
    };

    const submitAdd = async () => {
        if (!addName.trim()) return;
        setSaving(true);
        try {
            const payload: Record<string, unknown> = { name: addName.trim() };
            if (addParentId !== "") payload.parent_id = addParentId;
            await axiosInstant.post(
                adminRmnBrandCategoriesEndpoint(brandId),
                payload
            );
            toast.success("Category created");
            setAddOpen(false);
            setAddName("");
            setAddParentId("");
            await fetchTree();
        } catch (err) {
            toast.error("Failed to create category");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box className="flex justify-center py-16">
                <DotLottleLoader fill={false} />
            </Box>
        );
    }

    return (
        <Box className="flex flex-col gap-4">
            <Box className="flex flex-wrap items-center justify-between gap-3">
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Search categories"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 260 }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showDeactivated}
                                onChange={(e) => setShowDeactivated(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="body2">Show deactivated</Typography>
                        }
                    />
                </Stack>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<IconPlus />}
                    onClick={() => setAddOpen(true)}
                >
                    Add category
                </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell align="right">Products</TableCell>
                            <TableCell align="center">Active</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(({ node, depth, hasChildren }) => (
                            <TableRow
                                key={node.id}
                                hover
                                sx={node.deactivated ? { opacity: 0.5 } : undefined}
                            >
                                <TableCell>
                                    <Box
                                        className="flex items-center gap-1"
                                        sx={{ pl: `${depth * 24}px` }}
                                    >
                                        {hasChildren ? (
                                            <IconButton
                                                size="small"
                                                onClick={() => toggle(node.id)}
                                                sx={{ mr: 0.5 }}
                                            >
                                                {query || expanded.has(node.id) ? (
                                                    <ExpandMoreIcon fontSize="small" />
                                                ) : (
                                                    <ChevronRightIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        ) : (
                                            <Box sx={{ width: 34, flexShrink: 0 }} />
                                        )}
                                        <Typography
                                            sx={{ fontWeight: hasChildren ? 600 : 400 }}
                                        >
                                            {node.name}
                                        </Typography>
                                        {node.manual && (
                                            <Chip
                                                label="Manual"
                                                size="small"
                                                sx={{ ml: 1, height: 20 }}
                                            />
                                        )}
                                        {node.deactivated && (
                                            <Chip
                                                label="Deactivated"
                                                size="small"
                                                sx={{ ml: 1, height: 20 }}
                                            />
                                        )}
                                        {isExternalUrl(node.url) && (
                                            <IconButton
                                                size="small"
                                                component="a"
                                                href={node.url}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    {typeof node.product_count === "number"
                                        ? node.product_count
                                        : "—"}
                                </TableCell>
                                <TableCell align="center">
                                    <Switch
                                        size="small"
                                        checked={!node.deactivated}
                                        disabled={saving}
                                        onChange={() => toggleDeactivated(node)}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setRenameTarget(node);
                                            setRenameValue(node.name);
                                        }}
                                    >
                                        <IconPencil size={18} />
                                    </IconButton>
                                    {node.manual && (
                                        <IconButton
                                            size="small"
                                            onClick={() => setDeleteTarget(node)}
                                            sx={{ color: 'var(--text)' }}
                                        >
                                            {/* stroke via currentColor — SVG presentation attributes can't read var() */}
                                            <IconTrash width={18} height={18} stroke="currentColor" />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography className="py-6" color="var(--text-2)">
                                        {query
                                            ? "No categories match your search."
                                            : "No categories yet — run a crawl or add one manually."}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Rename dialog */}
            <Dialog
                open={!!renameTarget}
                fullWidth
                maxWidth="xs"
                onClose={() => !saving && setRenameTarget(null)}
            >
                <DialogTitle>Rename category</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                        label="Name"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                submitRename();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions className="!px-6 !py-4">
                    <Button
                        variant="outlined"
                        disabled={saving}
                        onClick={() => setRenameTarget(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving || !renameValue.trim()}
                        onClick={submitRename}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add dialog */}
            <Dialog
                open={addOpen}
                fullWidth
                maxWidth="xs"
                onClose={() => !saving && setAddOpen(false)}
            >
                <DialogTitle>Add category</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            label="Name"
                            value={addName}
                            onChange={(e) => setAddName(e.target.value)}
                        />
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Parent category"
                            value={addParentId}
                            onChange={(e) =>
                                setAddParentId(
                                    e.target.value === ""
                                        ? ""
                                        : Number(e.target.value)
                                )
                            }
                        >
                            <MenuItem value="">None (top level)</MenuItem>
                            {parentOptions.map((opt) => (
                                <MenuItem key={opt.id} value={opt.id}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions className="!px-6 !py-4">
                    <Button
                        variant="outlined"
                        disabled={saving}
                        onClick={() => setAddOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving || !addName.trim()}
                        onClick={submitAdd}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
                title="Delete category"
                description={`Delete "${deleteTarget ? deleteTarget.name : ""}"? Only manually added categories can be deleted.`}
                confirmLabel="Delete"
                disabled={saving}
            />
        </Box>
    );
}
